/**
 * AuthContext — profesyonel oturum yönetimi.
 *
 * Sorumluluklar:
 *  - Sayfa yüklendiğinde `/api/auth/me` ile state restore (cookie geçerliyse).
 *  - `login(...)`, `logout()`, `refreshNow()` exposları.
 *  - Silent refresh scheduler: access token expiry'sinden 60 sn önce arka planda /auth/refresh.
 *  - 401 axios interceptor: bir kere refresh dene, başarısızsa global logout.
 *  - Multi-tab sync: bir sekmede logout olunca diğerleri de logout.
 *
 * ÖNEMLİ: Token'lar JavaScript'e değmez (HTTP-only cookie). Sadece kullanıcı
 * görünen bilgileri (id, name, role) bellekte ve localStorage'da tutulur.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { BACKEND_URL } from "@/config";
import { buildApiCandidates, shouldFailoverBackendRequest } from "@/backendBases";
import {
  clearAuthSnapshot,
  migrateLegacyAuthStorage,
  readAuthSnapshot,
  writeAuthSnapshot,
  STORAGE_KEY_NAME,
} from "@/auth/authStorage";

// Runtime API base seçimi.
// NOT: `BACKEND_URL` (config.js'ten) bundle cache yüzünden eski değeri
// tutabilir. window.location üzerinden runtime'da karar verince
// cache problemi tamamen ortadan kalkar:
// - Dev'de (page localhost/127.0.0.1 ile yüklendiyse): API="" → tüm
//   istekler relative path olur → proxy üzerinden → same-origin → cookie OK.
// - Prod'da: BACKEND_URL kullan (env-driven).
function _computeApiBase() {
  if (typeof window === "undefined") return BACKEND_URL;
  try {
    const host = (window.location && window.location.hostname) || "";
    const isDevHost =
      host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0";
    if (isDevHost) return "";
  } catch (_) {
    /* ignore */
  }
  return BACKEND_URL;
}
const API = _computeApiBase();

// DEBUG: API/BACKEND_URL runtime değerlerini expose et.
if (typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.log(
    `[AUTH-DEBUG-FE] BACKEND_URL="${BACKEND_URL}" API="${API}" ` +
    `NODE_ENV="${process.env.NODE_ENV}" REACT_APP_BACKEND_URL="${process.env.REACT_APP_BACKEND_URL || ""}" ` +
    `host="${window.location?.hostname || ""}"`
  );
  window.__DEBUG_API__ = { BACKEND_URL, API, NODE_ENV: process.env.NODE_ENV };
}

// Access token expiry'den X ms önce silent refresh tetikle
const REFRESH_LEAD_TIME_MS = 60 * 1000;
// Refresh fail olduğunda kısa "geri çekilme" süresi (sürekli retry'a girmeyelim)
const REFRESH_BACKOFF_MS = 5 * 1000;

const AuthContext = createContext(null);

function _toEpoch(iso) {
  if (!iso) return 0;
  try {
    const t = new Date(iso).getTime();
    return Number.isFinite(t) ? t : 0;
  } catch (_) {
    return 0;
  }
}

async function _postWithFailover(pathOrUrl, body, opts = {}) {
  const candidates = buildApiCandidates(API);
  let lastErr = null;
  for (const base of candidates) {
    const url = base ? `${base}${pathOrUrl}` : pathOrUrl;
    try {
      const res = await axios.post(url, body, { withCredentials: true, ...opts });
      return res;
    } catch (err) {
      lastErr = err;
      if (shouldFailoverBackendRequest(err)) continue;
      throw err;
    }
  }
  throw lastErr || new Error("Sunucuya ulaşılamadı.");
}

async function _getWithFailover(pathOrUrl, opts = {}) {
  const candidates = buildApiCandidates(API);
  let lastErr = null;
  for (const base of candidates) {
    const url = base ? `${base}${pathOrUrl}` : pathOrUrl;
    try {
      const res = await axios.get(url, { withCredentials: true, ...opts });
      return res;
    } catch (err) {
      lastErr = err;
      if (shouldFailoverBackendRequest(err)) continue;
      throw err;
    }
  }
  throw lastErr || new Error("Sunucuya ulaşılamadı.");
}

export function AuthProvider({ children }) {
  // Aktif state
  const [user, setUser] = useState(() => {
    migrateLegacyAuthStorage();
    const snap = readAuthSnapshot();
    if (snap && snap.isLoggedIn) {
      return {
        personnelId: snap.personnelId || "",
        personnelName: snap.personnelName || "",
        userRole: snap.userRole || "",
        userRoleRaw: snap.userRoleRaw || "",
        credentialSetupPending: Boolean(snap.credentialSetupPending),
        sessionId: snap.sessionId || null,
      };
    }
    return null;
  });
  const [accessExpiresAt, setAccessExpiresAt] = useState(() => {
    const snap = readAuthSnapshot();
    return snap?.accessExpiresAt || null;
  });
  const [idleSettings, setIdleSettings] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  const refreshTimerRef = useRef(null);
  const inFlightRefreshRef = useRef(null);
  const lastFailedRefreshRef = useRef(0);

  // ---------- helpers ----------

  const _persistUser = useCallback((u, accessExpIso) => {
    if (!u) {
      clearAuthSnapshot();
      return;
    }
    writeAuthSnapshot({
      isLoggedIn: true,
      userRole: u.userRole || "",
      userRoleRaw: u.userRoleRaw || "",
      personnelId: u.personnelId || "",
      personnelName: u.personnelName || "",
      accessExpiresAt: accessExpIso || null,
      credentialSetupPending: Boolean(u.credentialSetupPending),
      rememberMe: u.rememberMe !== false,
      sessionId: u.sessionId || null,
    });
  }, []);

  const _scheduleNextRefresh = useCallback((expIso) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    const expEpoch = _toEpoch(expIso);
    if (!expEpoch) return;
    const delay = Math.max(1000, expEpoch - Date.now() - REFRESH_LEAD_TIME_MS);
    refreshTimerRef.current = setTimeout(() => {
      refreshNowRef.current && refreshNowRef.current();
    }, delay);
  }, []);

  const _applyAuthState = useCallback(
    (payload) => {
      const next = {
        personnelId: String(payload.personnel_id || payload.personnelId || ""),
        personnelName: String(payload.name || payload.personnelName || ""),
        userRole: String(payload.role || payload.userRole || ""),
        // role_raw: backend TB-047 alias'ından önceki gerçek DB rolü.
        // F1-D: `role`a DÜŞMEZ — Başkan'ın `role`u "admin" alias'ıdır; düşmek
        // Başkan'ı Süper Admin paneline (/admin) sokuyordu. Eksikse boş kalır
        // (fail-closed); /auth/me her zaman role_raw döndürür.
        userRoleRaw: String(payload.role_raw || payload.userRoleRaw || ""),
        credentialSetupPending: Boolean(
          payload.credential_setup_required ?? payload.credentialSetupPending
        ),
        sessionId: payload.session_id || payload.sessionId || null,
        rememberMe: payload.remember_me !== false,
      };
      const expIso = payload.access_expires_at || payload.accessExpiresAt || null;
      setUser(next);
      setAccessExpiresAt(expIso);
      if (payload.idle_settings) setIdleSettings(payload.idle_settings);
      _persistUser(next, expIso);
      _scheduleNextRefresh(expIso);
    },
    [_persistUser, _scheduleNextRefresh]
  );

  const _clearAuth = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    setUser(null);
    setAccessExpiresAt(null);
    clearAuthSnapshot();
  }, []);

  // ---------- public API ----------

  const login = useCallback(
    async (loginPayload) => {
      // loginPayload: response.data of POST /api/auth/login
      _applyAuthState(loginPayload);
      return loginPayload;
    },
    [_applyAuthState]
  );

  const logout = useCallback(async ({ silent = false } = {}) => {
    try {
      await _postWithFailover("/api/auth/logout", {}, { timeout: 10000 });
    } catch (_) {
      // bağlantı hatası bile olsa local state'i temizliyoruz
    }
    _clearAuth();
    if (!silent) {
      try {
        // Diğer sekmelerin senkron olması için global event
        window.dispatchEvent(new Event("parkBakimAuthCleared"));
      } catch (_) { /* ignore */ }
    }
  }, [_clearAuth]);

  const refreshNow = useCallback(async () => {
    // Aynı anda birden fazla refresh çağrısı varsa bunları tek bir promise'a bağla.
    if (inFlightRefreshRef.current) return inFlightRefreshRef.current;
    if (Date.now() - lastFailedRefreshRef.current < REFRESH_BACKOFF_MS) {
      return null;
    }
    const promise = (async () => {
      try {
        const res = await _postWithFailover("/api/auth/refresh", {}, { timeout: 15000 });
        const data = res?.data || {};
        const newExp = data.access_expires_at || null;
        setAccessExpiresAt(newExp);
        // user objemiz değişmedi, sadece expiry yenilendi
        if (user) {
          _persistUser(user, newExp);
          _scheduleNextRefresh(newExp);
        }
        return data;
      } catch (err) {
        lastFailedRefreshRef.current = Date.now();
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          _clearAuth();
          try { window.dispatchEvent(new Event("parkBakimAuthCleared")); } catch (_) { /* ignore */ }
        }
        return null;
      } finally {
        inFlightRefreshRef.current = null;
      }
    })();
    inFlightRefreshRef.current = promise;
    return promise;
  }, [_clearAuth, _persistUser, _scheduleNextRefresh, user]);

  // refreshNow'a setTimeout içinden ulaşmak için ref tutuyoruz (closure güncellemesi)
  const refreshNowRef = useRef(refreshNow);
  useEffect(() => {
    refreshNowRef.current = refreshNow;
  }, [refreshNow]);

  // ---------- sayfa yenileme: /auth/me ile state restore ----------

  useEffect(() => {
    let alive = true;
    (async () => {
      // Önce: localStorage'da auth snapshot var mı? Yoksa kullanıcı hiç giriş
      // yapmamış demektir; /api/auth/me ve /api/auth/refresh çağrılarını
      // boş yere atıp console'u kirletmeye gerek yok.
      const snap = readAuthSnapshot();
      if (!snap || !snap.isLoggedIn) {
        if (alive) setBootstrapping(false);
        return;
      }

      try {
        const res = await _getWithFailover("/api/auth/me", { timeout: 12000 });
        if (!alive) return;
        const data = res?.data || {};
        _applyAuthState(data);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          // Snapshot var ama cookie geçersiz/expired → refresh dene
          try {
            const refreshed = await _postWithFailover("/api/auth/refresh", {}, { timeout: 12000 });
            if (refreshed?.data) {
              const meRes = await _getWithFailover("/api/auth/me", { timeout: 12000 });
              if (alive && meRes?.data) {
                _applyAuthState(meRes.data);
                return;
              }
            }
          } catch (_) { /* refresh fail → temiz logout */ }
          if (alive) _clearAuth();
        } else if (alive) {
          // Network hatası — local snapshot ile devam et (best-effort)
        }
      } finally {
        if (alive) setBootstrapping(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // ---------- multi-tab sync (storage event) ----------

  useEffect(() => {
    const onStorage = (ev) => {
      if (ev.key === STORAGE_KEY_NAME) {
        if (!ev.newValue) {
          _clearAuth();
        }
      }
    };
    const onCleared = () => _clearAuth();
    window.addEventListener("storage", onStorage);
    window.addEventListener("parkBakimAuthCleared", onCleared);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("parkBakimAuthCleared", onCleared);
    };
  }, [_clearAuth]);

  // ---------- 401 axios interceptor: bir kere refresh dene ----------

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      async (err) => {
        const status = err?.response?.status;
        const originalReq = err?.config || {};
        // Refresh endpoint'inin kendisinde retry yapma (sonsuz döngü)
        const url = String(originalReq.url || "");
        if (url.includes("/api/auth/refresh") || url.includes("/api/auth/login") || url.includes("/api/auth/logout")) {
          return Promise.reject(err);
        }
        if (status === 401 && !originalReq.__pbAuthRetried) {
          originalReq.__pbAuthRetried = true;
          const refreshed = await refreshNow();
          if (refreshed) {
            try {
              return await axios(originalReq);
            } catch (e2) {
              return Promise.reject(e2);
            }
          }
        }
        if (status === 401) {
          _clearAuth();
          try { window.dispatchEvent(new Event("parkBakimAuthCleared")); } catch (_) { /* ignore */ }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [refreshNow, _clearAuth]);

  // ---------- aktivite (idle reset için kullanılan API) ----------

  const noteUserActivity = useCallback(() => {
    // IdleTimeoutManager kendi içinde lokalde timer'ı reset eder; bu hook
    // gelecekte server-side touch endpoint için kullanılabilir.
  }, []);

  const value = useMemo(
    () => ({
      isLoggedIn: Boolean(user),
      user,
      accessExpiresAt,
      idleSettings,
      bootstrapping,
      login,
      logout,
      refreshNow,
      noteUserActivity,
      setIdleSettings,
    }),
    [user, accessExpiresAt, idleSettings, bootstrapping, login, logout, refreshNow, noteUserActivity]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth, <AuthProvider> içinde kullanılmalıdır.");
  }
  return ctx;
}

export default AuthContext;
